import React, { useMemo } from 'react';
import { Technology, ResourceType } from '@/hooks/useResourceOptions';

interface TechnologyListProps {
    technologies: Technology[];
    types: ResourceType[];
    selectedTechnologyId?: string;
    onSelect: (technology: Technology) => void;
    loading: boolean;
    error: string | null;
    disabled?: boolean;
    isSearching: boolean;
}

const TechnologyList: React.FC<TechnologyListProps> = ({
    technologies,
    types,
    selectedTechnologyId,
    onSelect,
    loading,
    error,
    disabled = false,
    isSearching
}) => {
    const typeById = useMemo(() => {
        return types.reduce<Record<string, ResourceType>>((acc, type) => {
            acc[type.id] = type;
            return acc;
        }, {});
    }, [types]);

    if (loading) {
        return (
            <div className="border border-gray-200 rounded-md p-4 text-sm text-gray-500 bg-gray-50">
                Carregando tecnologias...
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-red-200 rounded-md p-4 text-sm text-red-600 bg-red-50">
                {error}
            </div>
        );
    }

    if (!technologies.length) {
        const message = isSearching
            ? 'Nenhuma tecnologia corresponde à sua busca.'
            : 'Nenhuma tecnologia encontrada para esse tipo. Verifique o cadastro no painel administrativo.';
        return (
            <div className="border border-gray-200 rounded-md p-4 text-sm text-gray-500 bg-gray-50">
                {message}
            </div>
        );
    }

    return (
        <div className="border border-gray-200 rounded-md bg-white">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                    Tecnologias relacionadas
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    Escolha uma tecnologia para compor a capacidade selecionada.
                </p>
            </div>
            <div
                role="listbox"
                aria-label="Tecnologias disponíveis"
                className="max-h-60 overflow-y-auto"
            >
                {technologies.map((technology) => {
                    const isSelected = technology.id === selectedTechnologyId;
                    return (
                        <button
                            key={technology.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            disabled={disabled}
                            onClick={() => onSelect(technology)}
                            className={`w-full text-left px-3 py-2 transition-colors border-b border-gray-100 last:border-b-0 ${
                                isSelected
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'hover:bg-gray-50 text-gray-700'
                            } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            title={technology.description || technology.name}
                        >
                            <span className="block text-sm font-medium">
                                {technology.name}
                            </span>
                            <span className="block text-xs text-gray-500 mt-1">
                                {typeById[technology.resourceTypeId]?.name || 'Tipo não definido'}
                            </span>
                            {technology.description ? (
                                <span className="block text-xs text-gray-500 mt-1">
                                    {technology.description}
                                </span>
                            ) : (
                                <span className="block text-xs text-gray-400 mt-1">
                                    Sem descrição cadastrada.
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default TechnologyList;
